import * as yup from 'yup';
import { FormattedMessage } from 'react-intl';

/**
 * The message yup 0.28 gave a null value in a string, number or boolean schema that is not
 * nullable and has no own type error message: its type error (for a number, about the NaN it cast
 * null to). yup 1 gives the required message or "cannot be null" instead, so the schemas this
 * applied to keep it with `.nonNullable(nullTypeError(type))`.
 */
// Bug: the English yup text reaches the user, e.g. when such a number input is cleared (null).
export const nullTypeError =
  (type: 'string' | 'number' | 'boolean') =>
  ({ path }: { path: string }) => {
    if (type === 'number') {
      return `${path} must be a \`number\` type, but the final value was: \`NaN\` (cast from the value \`NaN\`).`;
    }
    return `${path} must be a \`${type}\` type, but the final value was: \`null\`.\n If "null" is intended as an empty value be sure to mark the schema as \`.nullable()\``;
  };

/** `yup.mixed().notRequired()`, the base of the conditional feature validators. */
type TNotRequiredMixedSchema = ReturnType<ReturnType<typeof yup.mixed>['notRequired']>;

export const createEquipmentTypeValidator = (featureName: string) =>
  yup
    .mixed()
    .notRequired()
    .when(`features.${featureName}`, {
      is: true,
      then: (schema) =>
        schema.concat(
          yup
            .string()
            .required(() => <FormattedMessage id="errors.choose_one" />)
            .typeError(() => <FormattedMessage id="errors.choose_one" />),
        ),
    });

// The `then` branch of createConditionalValidator: the base schema joined with the required string
// or number schema (yup 0.28 joined a `then` schema with the base the same way).
const requireValue = (schema: TNotRequiredMixedSchema, type: string) => {
  if (type === 'string') {
    return schema.concat(
      yup
        .string()
        .required(() => <FormattedMessage id="errors.fill_field" />)
        .typeError(() => <FormattedMessage id="errors.fill_field" />),
    );
  }
  return schema.concat(
    yup
      .number()
      .required(() => <FormattedMessage id="errors.fill_field" />)
      .typeError(() => <FormattedMessage id="errors.fill_field" />),
  );
};

export const createConditionalValidator = (featureName: string, type = 'string') =>
  yup
    .mixed()
    .notRequired()
    .when(`features.${featureName}`, {
      is: true,
      then: (schema) => requireValue(schema, type),
    });
